import { useEffect, useState } from 'react';

// The rotating lead examples for heroes and the tour (founder decision, 20 Oct 2026):
// vary the example instead of one niche machine. All brands, people and serials are fictional.
// No user choice: heroes cycle automatically (cut, don't fade), reduced motion shows the first.

export type RangeKey = 'lift' | 'battery' | 'robotmower' | 'icemaker' | 'coffee' | 'hottub';

export type HeroUnit = {
  key: 'heatpump' | 'mower' | 'oven' | 'ebike' | RangeKey;
  noun: string; // headline word: "Know who owns every <noun> you have ever sold."
  brand: string;
  model: string;
  kind: string;
  serial: string;
  partnerRole: 'installer' | 'dealer' | 'service partner';
  partner: string;
  owner: string;
  owner2: string;
  handoverName: string; // what starts the warranty
  checks: string[];
  answer: string;
  source: string;
  part: string;
  product: string; // clean catalogue shot (phone / card)
  scene: string; // in-use photograph
  sceneAlt: string;
  accent: string; // brand accent for the fictional maker
};

export const heroUnits: HeroUnit[] = [
  {
    key: 'heatpump',
    noun: 'heat pump',
    brand: 'Halvard',
    model: 'Halvard AW-9',
    kind: 'Air source heat pump',
    serial: 'KAW9-26-04182',
    partnerRole: 'installer',
    partner: 'Brightwell Heating',
    owner: '14 Orchard Lane',
    owner2: 'The new owners, 14 Orchard Lane',
    handoverName: 'Commissioning',
    checks: ['Refrigerant pressures checked', 'Flow temperature set', 'Controls paired', 'Homeowner walkthrough'],
    answer: 'For radiators, set the flow temperature to 45°C and leave it running steadily.',
    source: 'Installation manual p.18',
    part: 'Fan motor assembly',
    product: '/images/v2/gen/unit-heatpump.jpg',
    scene: '/images/v2/gen/hp-house-dusk.jpg',
    sceneAlt: 'A brick house at dusk with a heat pump installed at the side',
    accent: '#2E6F6A',
  },
  {
    key: 'mower',
    noun: 'ride-on mower',
    brand: 'Moorcroft',
    model: 'Moorcroft RM-42',
    kind: 'Ride-on lawn mower',
    serial: 'MRM42-26-03317',
    partnerRole: 'dealer',
    partner: 'Fenwick Garden Machinery',
    owner: 'Ashcombe House',
    owner2: 'P. and D. Lowry',
    handoverName: 'Pre-delivery inspection',
    checks: ['Blades and deck checked', 'Oil and fuel levels set', 'Seat safety switch tested', 'Owner walkthrough'],
    answer: 'If it will not start, check the seat safety switch and the battery first; the engine only cranks with someone in the seat.',
    source: "Owner's manual p.24",
    part: 'Starter battery',
    product: '/images/v2/gen/unit-ridemower.jpg',
    scene: '/images/v2/gen/mower-hero.jpg',
    sceneAlt: 'A woman mowing a large lawn at a country house on a green ride-on mower on a spring morning',
    accent: '#2F5A3A',
  },
  {
    key: 'oven',
    noun: 'combi oven',
    brand: 'Fornell',
    model: 'Fornell CX-10',
    kind: 'Commercial combi oven',
    serial: 'FCX10-26-00217',
    partnerRole: 'service partner',
    partner: 'Northline Catering Services',
    owner: 'Harbour Street Kitchen',
    owner2: 'Harbour Street Kitchen, new owners',
    handoverName: 'Installation sign-off',
    checks: ['Electrical safety check', 'Core probe calibrated', 'Steam system tested', 'Staff trained'],
    answer: 'Descale the steam generator every 200 hours in hard-water areas.',
    source: 'Service manual p.36',
    part: 'Steam generator element',
    product: '/images/v2/gen/unit-combi-oven.jpg',
    scene: '/images/v2/gen/kitchen-combi.jpg',
    sceneAlt: 'A chef sliding a tray into a stainless steel combi oven in a busy restaurant kitchen',
    accent: '#8A5A2B',
  },
  {
    key: 'ebike',
    noun: 'e-bike',
    brand: 'Velora',
    model: 'Velora T8',
    kind: 'Electric step-through bike',
    serial: 'VT8-26-18842',
    partnerRole: 'dealer',
    partner: 'Canal Street Cycles',
    owner: 'Maya, Leith',
    owner2: 'Sam, Portobello',
    handoverName: 'Setup and handover',
    checks: ['Frame number recorded', 'Battery charged and registered', 'Brakes and gears adjusted', 'Rider fit and walkthrough'],
    answer: 'Range drops in cold weather and as the battery ages. Store it indoors, charge it to 80% for daily rides, and ask your dealer for a battery health check.',
    source: 'Battery manual p.9',
    part: 'Battery health check',
    product: '/images/v2/gen/unit-ebike.jpg',
    scene: '/images/v2/gen/ebike-hero.jpg',
    sceneAlt: 'A woman riding a slate blue step-through e-bike along a city bike lane in morning light',
    accent: '#33506B',
  },
];

// The homepage range (user, 20 Oct 2026): products people resonate with and that need looking after.
// The four above stay as the worked example on the tour, platform and AI pages.
export const rangeUnits: HeroUnit[] = [
  {
    // Same fictional maker and unit as the lifting and access equipment sector page.
    key: 'lift',
    noun: 'scissor lift',
    brand: 'Talbrook',
    model: 'Talbrook TX12',
    kind: 'Scissor lift, MEWP',
    serial: 'TB-TX12-2023-1186',
    partnerRole: 'service partner',
    partner: 'Pennine Lift Services',
    owner: 'Northgate Hire',
    owner2: 'Calder Access Hire',
    handoverName: 'Pre-delivery inspection',
    checks: ['Structure and welds', 'Hydraulics and hoses', 'Emergency lowering', 'Overload sensing'],
    answer: 'Use the emergency lowering valve at the rear of the chassis. Pull and hold it until the platform is fully down.',
    source: 'Operator manual p.27',
    part: 'Pothole guard',
    product: '/images/v2/gen/unit-scissor-lift.jpg',
    scene: '/images/v2/gen/lifting-inspection.jpg',
    sceneAlt: 'An engineer in a hi-vis vest inspecting a scissor lift in a bright warehouse',
    accent: '#1B1B1B',
  },
  {
    key: 'battery',
    noun: 'home battery',
    brand: 'Aurel',
    model: 'Aurel H13',
    kind: 'Home battery',
    serial: 'AH13-26-07514',
    partnerRole: 'installer',
    partner: 'Northgate Solar',
    owner: '27 Mill Road',
    owner2: 'The new owners, 27 Mill Road',
    handoverName: 'Commissioning',
    checks: ['Battery and inverter tested', 'Backup circuits tested', 'Charging schedule set', 'Homeowner walkthrough'],
    answer: 'Charge it on the overnight rate and keep a 20% reserve for power cuts.',
    source: 'Owner guide p.11',
    part: 'Backup gateway relay',
    product: '/images/v2/gen/unit-homebattery.jpg',
    scene: '/images/v2/gen/scene-homebattery.jpg',
    sceneAlt: 'A woman in her garage at home checking her phone beside a white home battery',
    accent: '#B5502E',
  },
  {
    key: 'robotmower',
    noun: 'robot mower',
    brand: 'Verdo',
    model: 'Verdo R7',
    kind: 'Robot lawn mower',
    serial: 'VR7-26-22609',
    partnerRole: 'dealer',
    partner: 'Hollins Garden Centre',
    owner: '6 Beech Close',
    owner2: 'The new owners, 6 Beech Close',
    handoverName: 'Installation and handover',
    checks: ['Base station placed', 'Lawn map drawn', 'No-go zones set', 'Owner walkthrough'],
    answer: 'Mow little and often: three short cuts a week at 4 cm keep the lawn thick.',
    source: "Owner's guide p.9",
    part: 'Replacement blade set',
    product: '/images/v2/gen/unit-robotmower.jpg',
    scene: '/images/v2/gen/scene-robotmower.jpg',
    sceneAlt: 'A robot mower crossing a striped lawn while a man watches from the patio with a coffee',
    accent: '#4F7A3A',
  },
  {
    key: 'icemaker',
    noun: 'ice machine',
    brand: 'Nordis',
    model: 'Nordis IM-150',
    kind: 'Commercial ice machine',
    serial: 'NIM150-26-01488',
    partnerRole: 'service partner',
    partner: 'Coldwell Refrigeration',
    owner: 'The Copper Room',
    owner2: 'Lantern Yard Bar',
    handoverName: 'Installation sign-off',
    checks: ['Water filter fitted', 'Drain tested', 'First harvest checked', 'Bar team trained'],
    answer: 'Clean and sanitise it every six months, and change the water filter at the same time.',
    source: 'Service manual p.22',
    part: 'Water filter cartridge',
    product: '/images/v2/gen/unit-icemaker.jpg',
    scene: '/images/v2/gen/scene-icemaker.jpg',
    sceneAlt: 'A bartender scooping ice from a stainless steel ice machine behind a busy bar',
    accent: '#2C6E9B',
  },
  {
    key: 'coffee',
    noun: 'coffee machine',
    brand: 'Corvino',
    model: 'Corvino E2',
    kind: 'Two-group espresso machine',
    serial: 'CE2-26-05531',
    partnerRole: 'service partner',
    partner: 'Tamperline Coffee Services',
    owner: 'Tidewater Café',
    owner2: 'Harbour Lights Café',
    handoverName: 'Installation sign-off',
    checks: ['Water filter fitted', 'Pressure and temperature set', 'Grinder dialled in', 'Baristas trained'],
    answer: 'Backflush with cleaner every evening, and change the water filter every six months.',
    source: 'Service manual p.30',
    part: 'Group head gasket set',
    product: '/images/v2/gen/unit-coffee.jpg',
    scene: '/images/v2/gen/scene-coffee.jpg',
    sceneAlt: 'A barista locking a portafilter into a stainless steel espresso machine in a bright café',
    accent: '#6B3E26',
  },
  {
    key: 'hottub',
    noun: 'hot tub',
    brand: 'Calder',
    model: 'Calder S6',
    kind: 'Six-seat hot tub',
    serial: 'CS6-26-03075',
    partnerRole: 'dealer',
    partner: 'Westbrook Spas',
    owner: 'The Old Barn',
    owner2: 'The new owners, The Old Barn',
    handoverName: 'Delivery and handover',
    checks: ['Electrical supply checked', 'Filled and balanced', 'Heater and jets tested', 'Owner walkthrough'],
    answer: 'Test the water twice a week and keep chlorine between 3 and 5 ppm.',
    source: "Owner's guide p.21",
    part: 'Filter cartridge pair',
    product: '/images/v2/gen/unit-hottub.jpg',
    scene: '/images/v2/gen/scene-hottub.jpg',
    sceneAlt: 'A couple relaxing in a hot tub on a garden deck at dusk, steam rising',
    accent: '#2A6F86',
  },
];

export const allUnits: HeroUnit[] = [...heroUnits, ...rangeUnits];

const prefersReduced = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** Cycles through heroUnits every `ms` (default 5s). Returns [unit, index]. Reduced motion: stays on the first. */
export function useHeroUnit(ms = 5000): [HeroUnit, number] {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (prefersReduced()) return;
    let t: number | undefined;
    const tick = () => {
      if (document.visibilityState === 'visible') setI((n) => (n + 1) % heroUnits.length);
      t = window.setTimeout(tick, ms);
    };
    t = window.setTimeout(tick, ms);
    return () => window.clearTimeout(t);
  }, [ms]);
  return [heroUnits[i], i];
}
